import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useState} from 'react';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenu} from '@Components/contextual-menu';
import {AudioItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/audio-item-controls';
import AiSubtitlePanel from '@Panels/ai-subtitle-panel/ai-subtitle-panel';
import {useAppSelector} from '@/hooks';
import styles from './audio-playlist-editing-options.module.scss';

enum AudioPlaylistEditingOptionsSubPanel {
  NONE = 'none',
  AI_SUBTITLES = 'ai-subtitles',
}

export function AudioPlaylistEditingOptions(): ReactElement<any> {
  const selectedAudioItemId = useAppSelector((state) => {
    const currentPageId = state?.posterEditor?.currentPageId;
    return state?.posterEditor?.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips.selectedAudioItemUID;
  });
  const selectedAudioItem =
    selectedAudioItemId &&
    window.posterEditor?.whiteboard?.getCurrentPage()?.audioClips?.audioItemsHashMap?.[selectedAudioItemId]?.toObject();

  if (!selectedAudioItem) {
    throw new Error('No selected audio item');
  }
  const [subPanel, setSubPanel] = useState(AudioPlaylistEditingOptionsSubPanel.NONE);

  const getHeaderForAiSubtitlesSubPanel = (): ReactElement<any> => {
    return (
      <ContextualMenuHeader id="audio-editing-panel-header">
        <ContextualMenuTextHeader
          showBackButton
          onBackButton={() => {
            setSubPanel(AudioPlaylistEditingOptionsSubPanel.NONE);
          }}
          text={window.i18next.t('pmwjs_ai_subtitles')}
        />
      </ContextualMenuHeader>
    );
  };

  const getHeader = (): ReactElement<any> => {
    if (subPanel === AudioPlaylistEditingOptionsSubPanel.AI_SUBTITLES) {
      return getHeaderForAiSubtitlesSubPanel();
    }
    return (
      <ContextualMenuHeader id="audio-editing-panel-header">
        <ContextualMenuTextHeader text={window.i18next.t('pmwjs_audio')} />
      </ContextualMenuHeader>
    );
  };

  const getAiSubtitlesSubPanel = (): ReactElement<any> => {
    return (
      <AiSubtitlePanel
        onDone={() => {
          setSubPanel(AudioPlaylistEditingOptionsSubPanel.NONE);
        }}
        onBack={() => {
          setSubPanel(AudioPlaylistEditingOptionsSubPanel.NONE);
        }}
        contentClasses={styles.content}
        hideHeader
      />
    );
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu id="audio-contextual-option">
        {subPanel === AudioPlaylistEditingOptionsSubPanel.AI_SUBTITLES ? getAiSubtitlesSubPanel() : <AudioItemControls />}
      </ContextualMenu>
    );
  }, [subPanel, selectedAudioItemId]);
}
